import {Component, NgZone, OnDestroy, OnInit} from '@angular/core';
import {NavigationStart, Router} from '@angular/router';
import {AudioManagement} from '@ionic-native/audio-management/ngx';
import {
  CameraPosition,
  Circle,
  Environment,
  GoogleMap,
  GoogleMaps,
  GoogleMapsEvent,
  HtmlInfoWindow,
  ILatLng,
  LocationService,
  MapType,
  Marker,
  Polyline,
  Spherical,
} from '@ionic-native/google-maps/ngx';
import {LaunchNavigator, LaunchNavigatorOptions} from '@awesome-cordova-plugins/launch-navigator/ngx';
import {Network} from '@awesome-cordova-plugins/network/ngx';
// import {OpenNativeSettings} from '@awesome-cordova-plugins/open-native-settings/ngx';
import {AlertController, Platform, ToastController} from '@ionic/angular';
import {AlertInput} from '@ionic/core';
// eslint-disable-next-line @typescript-eslint/no-redeclare
import {Storage} from '@ionic/storage-angular';
import {TranslateService} from '@ngx-translate/core';
import * as moment from 'moment';
import * as gpsDistance from 'gps-distance';
import {BehaviorSubject, combineLatest, interval, Observable, of, ReplaySubject, Subject, Subscription} from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  find,
  map,
  pairwise,
  shareReplay,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import {environment} from '../../../environments/environment';
import {GeocodingService} from '../../core/services/geocoding.service';
import {PushService} from '../../providers/push.service';
import {Device, Position} from '../../providers/traccar-client';
import {TrackerService} from '../../providers/tracker.service';
import {UnitConversionUtil} from '../../util/unit-conversion-util';
import {TraccarWebSocket} from '../../../app/core/services/traccar/traccar-web-socket';
import {DeviceService} from '../../../app/core/services/device.service';
import {WebSocketConnectionState} from '../../../app/core/models/web-socket/web-socket-connection-state';
import {SubscriptionService} from '../../providers/subscription.service';
import {UrlOpenerService} from '../../core/services/url-opener.service';
import {BikeService} from 'src/app/core/services/bike/bike.service';
import { UserService } from 'src/app/core/services/user.service';
import { Invitation } from 'src/app/core/models/invitation/pending-invitation';

@Component({
  selector: 'app-map',
  templateUrl: './map.html',
  styleUrls: ['./map.scss'],
})
export class MapPage implements OnInit, OnDestroy {
  BATTERY_RUNTIME_MOVEMENT = 13;
  BATTERY_RUNTIME_STANDBY = 20;
  STD_ZOOM = 16;
  MIN_SPEED = 2.7; // minimum reported speed to show it (knots), == 5 km/h
  DEFAULT_PIN_INDEX = 5;
  SELECTED_PIN_INDEX = 6;
  PIN_WIDTH = 35;
  PIN_HEIGHT = 90;
  PIN_CIRCLE_WIDTH = 21;
  PIN_CIRCLE_HEIGHT = 21;
  MARKER_MOVE_DURATION = 9000;
  MARKER_MOVE_TIMEOUT = 40;
  MARKER_REDUCEDPIN_LIMIT = 20;
  MARKER_ANIMATION_LIMIT = 20; // only animate 20 or less markers
  SELECTED_TRACK_INDEX = 7;

  brandName = environment.brandName;
  map: GoogleMap;
  pins: Map<number, Marker> = new Map();
  infoPin: Marker;
  track: Polyline;
  trackStart: Marker;
  geofence: Circle;
  htmlInfoWindow: HtmlInfoWindow;
  selectedDeviceId: number;
  wasInBackground = false;
  busy: boolean;
  lastBearing = 0;
  firstLoad = true;
  locationPermission = true;
  infoWindowOpen = false;
  mapType: MapType = 'MAP_TYPE_NORMAL';
  isWeb = environment.webPlatform;
  isForeground = true;
  creatingTrackPolyline = false;
  updatingPolyline = false;
  loadingDailyRoutes = false;

  resumeSub: Subscription;
  loadMapSub: Subscription;
  getTrackersSub: Subscription;
  centerOnSelectedSub: Subscription;
  onConnectSub: Subscription;
  onTrackClickSub: Subscription;
  // Data shown outside the map (titlebar + footer) of the currently selected tracker
  noTracker = this.trackers.getNoDevicesYetUpdate().pipe(shareReplay(1));
  public isWebsocketSlow$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isWebSocketReconnecting$ = this.traccarWebSocket.isReconnecting;
  public isWebSocketDisconnected$ = this.traccarWebSocket.connectionState$.pipe(
    map((connectionState) => connectionState === WebSocketConnectionState.DISCONNECTED),
  );
  selectedTracker = this.trackers.getSelected();
  selectedDevice = this.selectedTracker.pipe(
    switchMap((tracker) => tracker.getDevice()),
    shareReplay(1),
  );
  selectedName = this.selectedDevice.pipe(
    map((device) => device.name),
    distinctUntilChanged(),
  );
  selectedOnline = this.selectedDevice.pipe(
    map((device) => device.status === 'online'),
    distinctUntilChanged(),
  );
  selectedDisabled = this.selectedDevice.pipe(
    map((device) => device.disabled),
    distinctUntilChanged(),
  );
  selectedAttributes = this.selectedDevice.pipe(
    map((device) => device.attributes),
    shareReplay(1),
  );
  selectedGuarded = this.selectedAttributes.pipe(
    map((attributes) => attributes.guarded),
    distinctUntilChanged(),
  );
  selectedAlarm = this.selectedAttributes.pipe(
    map((attributes) => attributes.guarded && attributes.alarm),
    distinctUntilChanged(),
  );
  selectedStatusColor = this.selectedAttributes.pipe(
    map((attributes) => this.getGuardColor(attributes)),
    distinctUntilChanged(),
  );
  selectedGuardType = this.selectedAttributes.pipe(
    map((attributes) => attributes.guardType),
    distinctUntilChanged(),
  );
  selectedGpsDisabled = this.selectedAttributes.pipe(
    map((attributes) => attributes.gpsDisabled),
    distinctUntilChanged(),
  );
  selectedPosition = this.selectedTracker.pipe(switchMap((tracker) => tracker.getLastPosition()));
  selectedBatteryLevel = this.selectedPosition.pipe(
    filter((position) => !!position.attributes.batteryLevel),
    map((position) => this.trackers.estimateBattery(position)),
    distinctUntilChanged(),
    shareReplay(1),
  );
  selectedDailyDistance = 0;
  initialTracker = combineLatest([
    this.selectedPosition, // NOTE: also triggers for deleted tracker -> noTracker filter
    this.noTracker,
  ]).pipe(find(([, noTracker]) => !noTracker));
  selectedBatteryIcon = this.selectedPosition.pipe(
    filter((position) => !!position.attributes.batteryLevel),
    map((position) => this.trackers.generateBatteryIconPath(position)),
  );
  pendingInvitations: Invitation[] = [];
  hasPendingInvitations: boolean = false;

  // TODO: do not show statistics when external power connected (bike switched on)
  // batteryTooltip = combineLatest([
  //   this.selectedBatteryLevel,
  //   this.selectedOnline
  // ]).pipe(map(([batteryLevel, online]) => {
  //   if (batteryLevel <= 5) {
  //     return this.translate.instant('BATTERY_EMPTY'); // TODO: 'BATTERY_EMPTY' requires tracker name parameter
  //   }
  //   return this.translate.instant(online ? 'BATTERY_RUNTIME_MOVEMENT' : 'BATTERY_RUNTIME_STANDBY', {
  //     duration: Math.round((online ? this.BATTERY_RUNTIME_MOVEMENT : this.BATTERY_RUNTIME_STANDBY) * batteryLevel / 100)
  //   });
  // }));
  selectedCharging = this.selectedPosition.pipe(
    map((position) => position.attributes.charge),
    filter((charge) => !!charge),
    distinctUntilChanged(),
  );
  selectedSpeed = this.selectedPosition.pipe(
    map((position) => Math.round(this.unitUtil.convertValue(position.speed, 'velocity', 'kn'))),
    distinctUntilChanged(),
  );
  // show hint on android in case of silent/vibrate mode when turning guard on
  showSilentModeAlert = new ReplaySubject<boolean>(1);
  triggerSilentModeAlert = this.showSilentModeAlert.pipe(
    switchMap((showAlert) => this.selectedGuarded.pipe(filter(() => showAlert && this.platform.is('android')))),
    pairwise(),
    filter(([a, b]) => !a && b),
    switchMap(() => this.audioManagement.getAudioMode()),
    filter((value) => value.audioMode !== 2),
    switchMap(() =>
      this.alertCtrl.create({
        header: this.translate.instant('AUDIO_MODE'),
        message: this.translate.instant('AUDIO_MODE_ENABLED_HINT'),
        inputs: [
          {
            name: 'stopCheckbox',
            type: 'checkbox',
            label: this.translate.instant('PASSPORT_DIALOG_STOP_REMINDER'),
            value: 'stop',
          },
        ],
        buttons: [
          {
            text: 'OK',
            cssClass: 'secondary',
            handler: (value) => {
              if (value[0] === 'stop') {
                this.storage.set('showSilentModeAlert', false).then((show) => this.showSilentModeAlert.next(show));
              }
            },
          },
          {
            text: this.translate.instant('SETTINGS.TITLE'),
            handler: (value) => {
              if (value[0] === 'stop') {
                this.storage.set('showSilentModeAlert', false).then((show) => this.showSilentModeAlert.next(show));
              }
              // this.openNativeSettings.open('notification_id');
            },
          },
        ],
      }),
    ),
  );
  // show speed only if not older than 1 minutes and above 5km/h
  showSpeed = false;
  showPins = true;
  public readonly hasSufficientSubscription$: Observable<boolean> = this.selectedDevice.pipe(
    map(({attributes}) => this.subscriptionService.isSubscriptionSufficient(attributes)),
    distinctUntilChanged(),
    shareReplay(1),
  );
  // Assumption: As long as trackers exist, there is a tracker selected
  public readonly isAnyTrackerSelected$: Observable<boolean> = this.noTracker.pipe(
    map((hasNoTrackers) => !hasNoTrackers),
    startWith(false),
    distinctUntilChanged(),
    shareReplay(1),
  );
  public readonly toggleAlarm$: Subject<void> = new Subject();
  private readonly destroy$: Subject<void> = new Subject();
  private selectedBike$ = this.bikeService.selectedBike$;

  constructor(
    public alertCtrl: AlertController,
    public audioManagement: AudioManagement,
    private deviceService: DeviceService,
    public geocodingService: GeocodingService,
    public launchNavigator: LaunchNavigator,
    public network: Network,
    // public openNativeSettings: OpenNativeSettings,
    public platform: Platform,
    public push: PushService,
    public router: Router,
    public storage: Storage,
    private subscriptionService: SubscriptionService,
    public toastCtrl: ToastController,
    private traccarWebSocket: TraccarWebSocket,
    public trackers: TrackerService,
    public translate: TranslateService,
    public unitUtil: UnitConversionUtil,
    public zone: NgZone,
    private urlOpener: UrlOpenerService,
    private bikeService: BikeService,
    private userService: UserService,
  ) {
    // listen to pause + resume events so we do not animate the track
    document.addEventListener('pause', this.onPause.bind(this), false);
    document.addEventListener('resume', this.onResume.bind(this), false);

    this.bindToggleAlarmHandler();
  }

  async ngOnInit() {
    this.selectedTracker.subscribe((tracker) => this.handleTrackerSwitch(tracker));

    this.traccarWebSocket.heartBeatPending$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(async (wsHbPending) => {
      this.isWebsocketSlow$.next(wsHbPending);
      if (wsHbPending)
        console.log('Slow socket connection');
    });

    // See https://dzone.com/articles/understanding-ngzone for timeout issues during testing
    this.zone.runOutsideAngular(() =>
      combineLatest([interval(1000), this.selectedPosition])
        .pipe(
          map(
            ([, position]) =>
              moment().subtract(1, 'minute').isBefore(moment(position.serverTime)) && position.speed > this.MIN_SPEED,
          ),
        )
        .subscribe((showSpeed) => this.zone.run(() => (this.showSpeed = showSpeed))),
    );

    await this.platform.ready();

    this.htmlInfoWindow = new HtmlInfoWindow();
    this.htmlInfoWindow
      .on(GoogleMapsEvent.INFO_OPEN)
      .subscribe(() => this.zone.run(() => (this.infoWindowOpen = true)));
    this.htmlInfoWindow
      .on(GoogleMapsEvent.INFO_CLOSE)
      .subscribe(() => this.zone.run(() => (this.infoWindowOpen = false)));

    // low battery warning
    this.selectedBatteryLevel.pipe(
      withLatestFrom(this.selectedDevice),
      filter(([batteryLevel, device]) => batteryLevel <= 5),
      switchMap(([batteryLevel, device]) => this.toastCtrl.create({
        message: this.translate.instant('BATTERY_EMPTY', {name: device.name}),
        duration: 4000,
        buttons: [
          {
            text: 'X',
            role: 'cancel',
          },
        ],
      }))
    ).subscribe((toast) => toast?.present());

    // Once tracker is added, show "toggle alarm" tooltip on guard button and "tracker armed" toast when being armed for the first time
    this.router.events
      .pipe(
        // NavigationEnd does not work due to redirects from "/" to "/map"
        filter(
          (event) => event instanceof NavigationStart && event.url === '/' && this.router.url.startsWith('/configure'),
        ),
        switchMap(() => this.initialTracker),
        switchMap(() => this.selectedGuarded),
        filter((guarded) => guarded),
        take(1),
        switchMap(() =>
          this.toastCtrl.create({
            duration: 5000,
            message: this.translate.instant('TRACKER_ARMED_TOAST'),
            position: 'top',
          }),
        ),
      )
      .subscribe((toast) => toast.present());

    this.triggerSilentModeAlert.subscribe((alert) => alert.present());

    this.userService.getInvitations().subscribe(invitations=>{
      if(invitations.length > 0){
        this.hasPendingInvitations = true;
        this.pendingInvitations = invitations;
      }
    })
  }

  ionViewDidEnter() {
    // sync route when resuming from background
    this.resumeSub = this.platform.resume
      .pipe(
        switchMap(() => this.deviceService.refreshDevices()),
        filter(() => !!this.map),
      )
      .subscribe(() => this.updateRoute('platform.resume'));
    if (!this.map) {
      // load map once tracker position is available
      this.loadMapSub = this.initialTracker.subscribe(([position]) => this.loadMap(position));
    } else if (this.wasInBackground) {
      this.updateRoute('ionViewDidEnter');
    }

    this.storage.get('showSilentModeAlert').then((show) => this.showSilentModeAlert.next(show === null ? true : show));
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ionViewDidLeave() {
    this.resumeSub.unsubscribe();
    this.loadMapSub.unsubscribe(); // prevent map loading while in background with no map_canvas DIV available
    this.wasInBackground = !!this.map;
  }

  onPause() {
    this.isForeground = false;
  }

  onResume() {
    this.isForeground = true;

    // We don't animate the camera if the app is in background. We need to center the currently selected device again
    // once the app comes into foreground again in case it does not move anymore.
    this.selectedPosition.pipe(take(1)).subscribe((position) =>
      this.map?.animateCamera({
        target: {
          lat: position.latitude,
          lng: position.longitude,
        },
        duration: 500, // NOTE: this delays the marker animation
      }),
    );
  }

  async loadMap(center: Position) {
    await this.platform.ready();

    console.log('load map...', center);

    // get colour from :root via variable, note: trim() is required as we somehow get a leading whitespace...
    const colour = getComputedStyle(document.documentElement).getPropertyValue('--ion-color-primary').trim();

    Environment.setBackgroundColor(colour);
    this.firstLoad = false;
    if (environment.webPlatform) {
      Environment.setEnv({
        API_KEY_FOR_BROWSER_RELEASE: environment.mapsApiKey,
        API_KEY_FOR_BROWSER_DEBUG: environment.mapsApiKey,
      });
    }

    this.map = GoogleMaps.create('map_canvas', {
      camera: {
        target: {
          lat: center.latitude,
          lng: center.longitude,
        },
        zoom: this.STD_ZOOM,
      },
      controls: {
        compass: true,
        mapToolbar: false,
        zoom: environment.webPlatform,
      },
      mapType: (this.mapType = (await this.storage.get('mapType')) || this.mapType),
    });

    this.map.one(GoogleMapsEvent.MAP_READY).then(this.onMapReady.bind(this));
  }

  // NOTE: all permanent subscriptions (no take(1)) need to be cancelled when map gets destroyed (for later recreation)
  onMapReady() {
    this.showPins = this.trackers.getNumOfTrackers() <= this.MARKER_REDUCEDPIN_LIMIT;

    this.getTrackersSub = this.trackers.getTrackers().subscribe((tracker) => {
      this.addMarker(tracker);

      // Update marker location and extend polygon on position updates
      this.getTrackersSub = tracker
        .getLastPosition()
        .pipe(
          filter((position) => !!position.latitude && this.pins.has(position.deviceId)),
          tap((position) => this.updateMarkerPosition(position)),
        )
        .subscribe();

      // Update marker color on device updates
      tracker
        .getDevice()
        .pipe(
          filter((device) => this.pins.has(device.id)),
          tap((device) => this.updateMarkerDevice(device)),
          map((device) => this.getMarkerUrl(device)),
          distinctUntilChanged(),
        )
        .subscribe({
          next: (url) => {
            const pin = this.pins.get(tracker.id);
            pin.setIcon({
              url,
              size: {
                width: this.PIN_WIDTH,
                height: this.PIN_HEIGHT,
              },
            });
            pin.setIconAnchor(this.PIN_WIDTH / 2, this.PIN_HEIGHT / 2);
          },
          complete: () => this.deleteMarker(tracker.id),
        });
    });

    this.map.on(GoogleMapsEvent.MAP_CLICK).subscribe(() => {
      this.cleanTrackPin();
    });

    this.map.on(GoogleMapsEvent.CAMERA_MOVE).subscribe((cameraPosition: CameraPosition<ILatLng[]>) => {
      const bearing = parseInt(cameraPosition[0].bearing, 10);
      if (bearing !== this.lastBearing) {
        this.lastBearing = bearing;
        this.pins.forEach((marker: Marker) => marker.set('bearing', bearing));
      }
    });

    // Center on selected marker
    this.centerOnSelectedSub = this.selectedPosition.pipe(filter(() => this.isForeground)).subscribe((position) =>
      this.map.animateCamera({
        target: {
          lat: position.latitude,
          lng: position.longitude,
        },
        duration: 500, // NOTE: this delays the marker animation
      }),
    );

    // Handle geofence additon
    this.selectedTracker
      .pipe(
        switchMap((tracker) => tracker.getGeofence()),
        tap(() => this.geofence && this.geofence.remove()),
        filter((geofence) => !!geofence),
        switchMap((geofence) =>
          this.map.addCircle({
            center: {
              lat: geofence.latitude,
              lng: geofence.longitude,
            },
            radius: geofence.radius,
            fillColor: 'rgba(16, 220, 96, 0.25)',
            strokeColor: 'rgba(16, 220, 96, 0.5)',
            strokeWidth: 2,
            geofenceId: geofence.id,
          }),
        ),
      )
      .subscribe((circle) => {
        this.geofence = circle;
      });

    // Add initial tracker path - only if the tracker was moved that day, this will create a polyline
    // Otherwise polyline will be created once the first update is received
    this.onConnectSub = this.network
      .onConnect()
      .pipe(
        debounceTime(10000), // onConnect triggers several times when coming back online
        map(() => this.updateRoute('onConnect')),
      )
      .subscribe();

    this.noTracker.pipe(find((noTracker) => noTracker)).subscribe(() => {
      this.map.destroy();
      this.map = null;

      this.getTrackersSub.unsubscribe();
      this.centerOnSelectedSub.unsubscribe();
      this.onConnectSub.unsubscribe();
    });

    this.updateRoute('onMapReady');
  }

  updateRoute(caller) {
    this.selectedTracker
      .pipe(
        take(1),
        tap(() => {
          this.loadingDailyRoutes = true;
        }),
        switchMap((selectedTracker) => selectedTracker.getTodaysPositions(caller === 'platform.resume')),
        catchError((err) => {
          this.loadingDailyRoutes = false;
          console.error('getSelectedDevicePositions failed', err, caller);
          return of([]);
        }),
        tap(() => {
          this.loadingDailyRoutes = false;
        }),
        filter((positions) => !!positions[0]),
        tap(() => this.cleanRoute()), // Remove existing path whenever a new tracker is selected
        map((positions) => this.updatePolygon(positions)),
      )
      .subscribe((positions) => console.log('updateRoute | positions:', positions));
  }

  cleanRoute() {
    if (this.track) {
      this.cleanTrackPin();

      if (this.onTrackClickSub) {
        this.onTrackClickSub.unsubscribe();
        this.onTrackClickSub = null;
      }

      this.track.remove();
      this.track = null;
    }

    if (this.trackStart) {
      this.trackStart.remove();
    }
  }

  cleanTrackPin() {
    if (this.infoPin) {
      if (this.infoPin.isInfoWindowShown()) {
        this.htmlInfoWindow.close();
      }

      this.infoPin.remove();
      this.infoPin = null;
    }
  }

  addMarker(tracker) {
    combineLatest([tracker.getDevice(), tracker.getLastPosition()])
      .pipe(take(1))
      .subscribe(async ([device, position]: [Device, Position]) => {
        const armedAndAlert = device.attributes.guarded && device.attributes.alarm;
        const showPin = this.selectedDeviceId === position.deviceId || armedAndAlert ? true : this.showPins;

        // Add tracker pin
        const pin = await this.map.addMarker({
          icon: {
            url: this.getMarkerUrl(device),
            size: {width: this.PIN_WIDTH, height: this.PIN_HEIGHT},
          },
          position: {lat: position.latitude, lng: position.longitude},
          infoWindowAnchor: [this.PIN_WIDTH / 2 + 1, this.PIN_HEIGHT / 2],
          visible: showPin,
          zIndex: this.DEFAULT_PIN_INDEX,
        });
        pin.setIconAnchor(this.PIN_WIDTH / 2, this.PIN_HEIGHT / 2);

        // NOTE: if an already open infowindow on a marker is selected again, this triggers a refresh
        pin.on(GoogleMapsEvent.MARKER_CLICK).subscribe(() => this.zone.run(() => this.onMarkerClick(tracker, pin)));

        // This also triggers an update of the *_changed functions above
        pin.set('device', device);
        pin.set('speed', position.speed);
        pin.set('course', position.course);
        pin.set('bearing', this.map.getCameraBearing());
        pin.set('updated', position.fixTime);

        // fallback circle if too many trackers
        const circle = await this.map.addMarker({
          icon: {
            url: `${!environment.webPlatform && this.platform.is('ios') ? 'www' : '.'}/assets/img/basic.png`,
            size: {width: this.PIN_CIRCLE_WIDTH, height: this.PIN_CIRCLE_HEIGHT},
          },
          position: pin.getPosition(),
          visible: !showPin,
          zIndex: this.DEFAULT_PIN_INDEX - 1,
        });
        circle.setIconAnchor(this.PIN_CIRCLE_WIDTH / 2, this.PIN_CIRCLE_HEIGHT / 2);
        circle.on(GoogleMapsEvent.MARKER_CLICK).subscribe(() => this.zone.run(() => this.onMarkerClick(tracker, pin)));

        // Automatically update circle attributes when pin attributes change
        pin.bindTo('position', circle);

        pin.set('circle', circle);

        // Add tracker direction arrow
        // arrow binds to (custom) marker / pin attributes (position, speed, course) and is updated whenever pin attributes change
        const arrow = await this.map.addMarker({
          icon: {
            url: `${!environment.webPlatform && this.platform.is('ios') ? 'www' : '.'}/assets/img/map-arrow.png`,
            size: {width: 15, height: 15},
          },
          position: pin.getPosition(),
          visible: false,
          zIndex: this.DEFAULT_PIN_INDEX - 2,
        });
        arrow.on('speed_changed').subscribe((params) => arrow.setVisible(params[1] > this.MIN_SPEED)); // Hide arrow when speed is 0
        arrow.on('course_changed').subscribe((params) => {
          const bearing = arrow.get('bearing') || 0;
          arrow.setRotation(params[1] - bearing);
        });
        arrow.on('bearing_changed').subscribe((params) => {
          const course = arrow.get('course') || 0;
          arrow.setRotation(course - params[1]);
        });
        arrow.on(GoogleMapsEvent.MARKER_CLICK).subscribe(() => this.zone.run(() => this.onMarkerClick(tracker, pin)));

        // Automatically update arrow attributes when pin attributes change
        pin.bindTo('position', arrow);
        pin.bindTo('speed', arrow);
        pin.bindTo('course', arrow);
        pin.bindTo('bearing', arrow);

        pin.set('arrow', arrow);

        this.pins.set(position.deviceId, pin);
      });
  }

  handleTrackerSwitch(newTracker) {
    if (this.geofence) {
      this.geofence.remove();
    }

    const prevTrackerId = this.selectedDeviceId;
    this.selectedDeviceId = newTracker.id;

    const pinNew = this.pins.get(newTracker.id);

    this.cleanTrackPin();

    // pin does not yet exist
    if (!pinNew) {
      return;
    }

    // re-arrange trackers z-indexes
    pinNew.setZIndex(this.SELECTED_PIN_INDEX);
    this.cleanRoute(); // NOTE: because updateRoute() only removes old route if the new tracker sent positions today
    this.updateRoute('selectedTracker');

    // first switch after init --> show pin
    if (!prevTrackerId) {
      pinNew.setVisible(true);
      return;
    }

    // only act if enough trackers
    if (this.trackers.getNumOfTrackers() > this.MARKER_REDUCEDPIN_LIMIT) {
      const circleNew = pinNew.get('circle');
      const pinPrev = this.pins.get(prevTrackerId);
      const circlePrev = pinPrev.get('circle');

      const prevDevice = pinPrev.get('device');
      const prevArmedAndAlert = prevDevice.attributes.guarded && prevDevice.attributes.alarm;

      if (!prevArmedAndAlert) {
        // change pin/circle
        pinPrev.setVisible(false);
        circlePrev.setVisible(true);
      }

      pinNew.setVisible(true);
      circleNew.setVisible(false);
    }
  }

  getUserLocation() {
    LocationService.getMyLocation()
      .then((location) => {
        console.log('location', location);
        this.locationPermission = true;
        this.map.setMyLocationEnabled(true);
        this.map.animateCamera({
          target: location.latLng,
          duration: 1000,
        });
      })
      .catch((err) => {
        console.error('getMyLocation failed', err);
        this.locationPermission = false;
      });
  }

  onMarkerClick(tracker, marker) {
    if (this.selectedDeviceId !== tracker.id) {
      this.trackers.select(tracker.id);
    }
    this.cleanTrackPin();
    this.htmlInfoWindow.open(marker);
    this.updateInfoWindow(marker);
  }

  updateMarkerDevice(device) {
    const marker = this.pins.get(device.id);
    marker.set('device', device);
    if (marker.isInfoWindowShown()) {
      this.updateInfoWindow(marker);
    }

    // change pin/circle in case of alert
    if (device.attributes.guarded && device.attributes.alarm) {
      const circle = marker.get('circle');
      if (circle) {
        circle.setVisible(false);
      }
      marker.setVisible(true);
    }
  }

  updateMarkerPosition(position) {
    const marker = this.pins.get(position.deviceId);
    marker.set('speed', position.speed);
    marker.set('course', position.course);
    marker.set('updated', position.fixTime);
    if (marker.isInfoWindowShown()) {
      this.updateInfoWindow(marker);
    }

    const markerPos = {lat: position.latitude, lng: position.longitude};

    if (
      this.isForeground &&
      !(!environment.webPlatform && this.platform.is('ios')) &&
      this.pins.size <= this.MARKER_ANIMATION_LIMIT
    ) {
      this.animateMarkerTo(marker, markerPos);
    } else {
      // cancel animation if exists
      if (marker.get('moveSub')) {
        marker.get('moveSub').unsubscribe();
      }
      // just set the position without animation
      marker.setPosition(markerPos);

      if (position.deviceId === this.selectedDeviceId) {
        this.updatePolygon([position]);
      }
    }
  }

  animateMarkerTo(marker, end) {
    try {
      const start = marker.getPosition();
      // add new point and drag it along with animation
      if (marker.get('device')?.id === this.selectedDeviceId) {
        this.updatePolygon([{latitude: start.lat, longitude: start.lng}]);
      }
      if (marker.get('moveSub')) {
        marker.get('moveSub').unsubscribe();
      }
      marker.set(
        'moveSub',
        interval(this.MARKER_MOVE_TIMEOUT)
          .pipe(
            take(this.MARKER_MOVE_DURATION / this.MARKER_MOVE_TIMEOUT),
            map((step) =>
              Spherical.interpolate(start, end, step / (this.MARKER_MOVE_DURATION / this.MARKER_MOVE_TIMEOUT)),
            ),
          )
          .subscribe((position) => {
            marker.setPosition(position);

            if (!this.updatingPolyline && marker.get('device')?.id === this.selectedDeviceId) {
              this.updatePolygon([{latitude: position.lat, longitude: position.lng}], true);
            }
          }),
      );
    } catch (err) {
      console.error('Error while moving marker on map', err);
    }
  }

  deleteMarker(deviceId) {
    const marker = this.pins.get(deviceId);
    if (marker) {
      const arrow = marker.get('arrow');
      if (arrow) {
        arrow.remove();
      }
      if (marker.isInfoWindowShown()) {
        this.htmlInfoWindow.close();
      }
      marker.remove();
      this.pins.delete(deviceId);
    }
    // TODO: check if the route stays if there was only one tracker in the account (but map should be hidden then anyway)
  }

  // extend track polygon if update for selected tracker - otherwise create polygon
  updatePolygon(positions, updateLatest = false) {
    const points = positions.map((position) => ({lat: position.latitude, lng: position.longitude}));
    const dailyDistance = gpsDistance(positions.map((position) => [position.latitude, position.longitude]));
    this.selectedDailyDistance = Math.round(this.unitUtil.convertValue(dailyDistance, 'distance', 'km'));

    if (!this.track && points.length > 0) {
      this.createTrackPolyline(points);
    } else {
      const polyline = this.track.getPoints();
      const lineLength = polyline.getLength();

      if (updateLatest && positions.length === 1 && lineLength > 1) {
        this.updatingPolyline = true;
        polyline.setAt(lineLength - 1, points[0]);
        this.updatingPolyline = false;
      } else {
        points.forEach((point) => polyline.push(point));
      }
    }
  }

  getGuardColor(attributes) {
    if (attributes.guarded && attributes.alarm) {
      return 'danger';
    }
    if (attributes.guarded) {
      return 'success';
    }
    return 'warning';
  }

  getMarkerUrl(device) {
    let letter = device.name.toString().slice(0, 1).toLowerCase();
    if ('äöü'.includes(letter)) {
      letter = letter.replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue'); // replace umlaut if it is one
    }
    return (
      `${!environment.webPlatform && this.platform.is('ios') ? 'www' : '.'}/assets/img/markers/marker-` +
      `${this.getGuardColor(device.attributes)}-${letter}.png`
    );
  }

  async createTrackPolyline(points) {
    // do nothing in case map is not yet loaded
    // called multiple times by async function --> call only once
    if (!this.map || this.creatingTrackPolyline) {
      return;
    }

    this.creatingTrackPolyline = true;

    this.trackStart = await this.map.addMarker({
      icon: {
        url: `${!environment.webPlatform && this.platform.is('ios') ? 'www' : '.'}/assets/img/map-start.png`,
        size: {width: 20, height: 20},
      },
      position: points[0],
    });

    // get colour from :root via variable, note: trim() is required as we somehow get a leading whitespace...
    const color = getComputedStyle(document.documentElement).getPropertyValue('--map-track-color').trim();

    this.track = await this.map.addPolyline({
      points,
      color,
      width: 6,
      geodesic: true,
      zIndex: this.SELECTED_TRACK_INDEX,
      clickable: true,
    });

    this.onPolylineClick();
    this.creatingTrackPolyline = false;
  }

  onPolylineClick() {
    this.onTrackClickSub = this.track
      .on(GoogleMapsEvent.POLYLINE_CLICK)
      .pipe(tap(() => (this.infoPin ? (this.htmlInfoWindow.close(), this.infoPin.remove()) : null)))
      .subscribe(async (params: [ILatLng, Polyline]) => {
        const position: ILatLng = params[0];
        this.infoPin = await this.map.addMarker({
          icon: {
            size: {width: this.PIN_WIDTH, height: this.PIN_HEIGHT},
          },
          position,
          infoWindowAnchor: [this.PIN_WIDTH / 2 + 1, this.PIN_HEIGHT / 2],
          zIndex: this.DEFAULT_PIN_INDEX,
        });

        this.infoPin.setIconAnchor(this.PIN_WIDTH / 2, this.PIN_HEIGHT / 2);

        await this.map.animateCamera({
          target: {
            ...position,
          },
          duration: 500, // NOTE: this delays the marker animation
        });

        this.htmlInfoWindow.open(this.infoPin);
        this.updateInfoWindow(this.infoPin);
      });
  }

  async updateInfoWindow(marker) {
    this.setInfoWindowContent(marker);

    // Limit geocoding to native (which is free) to reduce Google bill
    if (this.isWeb) return;

    this.geocodingService.reverseGeocode(marker.getPosition().lat, marker.getPosition().lng).subscribe({
      next: (geocoderResult) => {

        const address: string = this.geocodingService.parseAddress(geocoderResult);

        marker.set('address', address);
        this.setInfoWindowContent(marker);
      },
      error: (error: any) => {
        console.error('Error while geocoding address for infowindow', error);
      },
    });
  }

  setInfoWindowContent(marker) {
    const mainElem = document.createElement('div');
    mainElem.classList.add('info-window');

    // ADDRESS
    if (marker.get('address')) {
      const addressElem = document.createElement('div');
      addressElem.textContent = marker.get('address');
      mainElem.appendChild(addressElem);
    }

    // LAST UPDATE
    const updatedElem = document.createElement('small');
    updatedElem.textContent = `${this.translate.instant('SINCE_TIMESTAMP', {
      relativeTime: moment(marker.get('updated')).calendar(),
    })}`;
    mainElem.appendChild(updatedElem);
    mainElem.appendChild(document.createElement('br'));

    // POSITION
    try {
      const positionElem = document.createElement('small');
      positionElem.textContent = `GPS: ${marker.getPosition().lat.toFixed(5)}, ${marker.getPosition().lng.toFixed(5)}`;
      mainElem.appendChild(positionElem);
      mainElem.appendChild(document.createElement('br'));
    } catch (err) {
      console.error('Error while setting GPS coordinates in infowindow content', err);
    }

    // TRACKER STATUS
    // TODO why can this be undefined? --> should not happen, fixed for now
    if (marker.get('device')) {
      const statusElem = document.createElement('small');
      if (marker.get('device').status === 'online') {
        statusElem.textContent = 'online';
      } else {
        const fromNowText = this.translate.instant('UPDATED_FROMNOW', {
          fromNow: moment(marker.get('device').lastUpdate).fromNow(),
        });
        statusElem.textContent = fromNowText;
      }
      mainElem.appendChild(statusElem);
      mainElem.appendChild(document.createElement('br'));
    }

    // set on info window
    this.htmlInfoWindow.setContent(mainElem);
  }

  openShop() {
    this.urlOpener.openUrl(environment.shopUrl);
  }

  openNavigator() {
    const options: LaunchNavigatorOptions = {
      app: this.launchNavigator.APP.USER_SELECT,
    };

    this.selectedPosition.pipe(take(1)).subscribe((position) => {
      this.launchNavigator.navigate([position.latitude, position.longitude], options).catch(async (err) => {
        console.error('An error occured trying to navigate', err);
        const alert = await this.alertCtrl.create({
          header: this.translate.instant('ERROR'),
          message: this.translate.instant('UNKNOWN_ERROR', {emailSupport: environment.emailSupport}),
          buttons: ['OK'],
        });
        await alert.present();
      });
    });
  }

  retrySocketConnection() {
    this.traccarWebSocket.refresh();
  }

  async setMapType() {
    const alert = await this.alertCtrl.create({
      header: this.translate.instant('MAP_TYPE'),
      inputs: ['MAP_TYPE_NORMAL', 'MAP_TYPE_HYBRID', 'MAP_TYPE_TERRAIN'].map(
        (mapType: MapType): AlertInput => ({
          type: 'radio',
          label: this.translate.instant(mapType),
          checked: mapType === this.mapType,
          handler: () => {
            this.mapType = mapType;
            this.map.setMapTypeId(mapType);
            this.storage.set('mapType', mapType);
            alert.dismiss();
          },
        }),
      ),
      buttons: [{text: 'Cancel'}],
    });
    await alert.present();
  }

  async showDailyDistanceToast() {
    const toast = await this.toastCtrl.create({
      duration: 5000,
      message: this.translate.instant('DAILY_DISTANCE_TOAST', {
        distance: this.selectedDailyDistance,
        unit: this.unitUtil.distanceDefault,
      }),
      position: 'top',
    });
    await toast.present();
  }

  public showBatteryEstimation(): void {
    this.selectedBike$.pipe(
      take(1),
      map((bike) => [bike.estimatedStandByTime, bike.estimatedBatteryDuration]))
      .subscribe(async ([estimationStandby, estimationMovement]) => {
        const message =
          `${this.translate.instant('BATTERY_ESTIMATION_MESSAGE')}<br><br>` +
          `${this.translate.instant('BATTERY_ESTIMATION_MESSAGE_STANDBY', {estimationStandby})}<br><br>` +
          `${this.translate.instant('OR')}<br><br>` +
          `${this.translate.instant('BATTERY_ESTIMATION_MESSAGE_MOVEMENT', {estimationMovement})}`;

        const alert = await this.alertCtrl.create({
          header: this.translate.instant('BATTERY_ESTIMATION_HEADER'),
          message,
          buttons: [
            {
              text: 'Ok',
              role: 'cancel',
            },
          ],
        });
        await alert.present();
      });
  }

  private bindToggleAlarmHandler(): void {
    this.toggleAlarm$.pipe(
      debounceTime(100),
      takeUntil(this.destroy$),
    ).subscribe(() => {
      this.handleToggleAlarm();
    });
  }

  private handleToggleAlarm(): void {
    this.busy = true;
    this.selectedAlarm
      .pipe(
        take(1),
        filter((alarm) => !!alarm),
      )
      .subscribe(() => this.push.stopAlarm());

    // check if sending pushes is possible when arming
    this.selectedGuarded
      .pipe(
        take(1),
        filter((guarded) => !guarded),
      )
      .subscribe(() => {
        this.push.checkPermissionAndTokens();
      });

    this.selectedTracker
      .pipe(
        take(1),
        switchMap((tracker) => tracker.toggleGuarded()),
      )
      .subscribe(
        () => {
          this.busy = false;
        },
        (err) => {
          console.error('toggleAlarm failed', err);
          // TODO: show toast?
          this.busy = false;
        },
      );
  }
}
